import React, { useState } from "react";
import { Input, Select, DatePicker, Button } from "antd";
import { FilterOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;

interface FilterField {
  key: string;
  label: string;
  type: "text" | "select" | "date" | "status";
  options?: { value: string | number | boolean; label: string }[];
}

interface UserFiltersProps {
  fields: FilterField[];
  onApplyFilters: (filters: Record<string, any>) => void;
  onClearFilters: () => void;
  defaultFilters: Record<string, any>; // Add this to receive default filters
}

const UserFilters: React.FC<UserFiltersProps> = ({
  fields,
  onApplyFilters,
  onClearFilters,
  defaultFilters,
}) => {
  const [filters, setFilters] = useState<Record<string, any>>(defaultFilters);

  const handleFilterChange = (key: string, value: any) => {
    // Special handling for user_type_id to ensure it's a number
    if (key === "user_type_id" && value !== undefined && value !== "") {
      const numericValue = Number(value);
      console.log(`Converting user_type_id from ${value} to ${numericValue}`);
      setFilters((prev) => ({ ...prev, [key]: numericValue }));
      return;
    }

    // Handle empty values properly
    if (value === undefined || value === "") {
      const newFilters = { ...filters };
      delete newFilters[key];
      setFilters(newFilters);
      console.log(`Removed empty filter: ${key}`);
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
      console.log(`Set filter ${key} to:`, value);
    }
  };

  const handleClearFilters = () => {
    // Reset to default filters
    setFilters(defaultFilters);
    onClearFilters();
  };

  return (
    <div className="bg-white p-4 shadow-md rounded-lg space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {fields.map(({ key, label, type, options }) => (
          <div key={key}>
            {type === "text" && (
              <Input
                placeholder={label}
                value={filters[key] || ""}
                onChange={(e) => handleFilterChange(key, e.target.value)}
              />
            )}
            {type === "select" && (
              <Select
                placeholder={label}
                value={filters[key]}
                onChange={(value) => handleFilterChange(key, value)}
                className="w-full"
                options={options}
                allowClear
              />
            )}
            {type === "date" && (
              <RangePicker
                className="w-full"
                value={filters[key]}
                onChange={(dates) =>
                  handleFilterChange(
                    key,
                    dates?.map((d) => d?.format("YYYY-MM-DD")) ?? []
                  )
                }
              />
            )}
            {type === "status" && (
              <Select
                placeholder="Status"
                value={filters[key]}
                className="w-full"
                onChange={(value) => handleFilterChange(key, value)}
                options={[
                  { value: "1", label: "Active" },
                  { value: "0", label: "Inactive" },
                ]}
                allowClear
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end space-x-3">
        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={() => onApplyFilters(filters)}
        >
          Apply Filters
        </Button>
        <Button onClick={handleClearFilters}>Clear Filters</Button>
      </div>
    </div>
  );
};

export default UserFilters;
